/**
 * Drop adapters: DropsWithStatsRow → GenericStatsRow
 * For Media Devoted | Created By Ciro Autiero
 */

import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { GenericStatsRow } from '../../shared/generic-stats.types';
import { getDropLogicalDateISO, getDropOrderKey, orDash } from '../../shared';

/**
 * Collapse duplicate API rows sharing the same drop uniqueId.
 * Prefers the row whose logical date matches the stats work day so the same drop
 * is not shown once as “today” and again under “other” (e.g. mixed executeAfter vs UTC dates).
 */
export function dedupeDropsWithStatsByUniqueId(
  rows: DropsWithStatsRow[],
  workDate: string,
): DropsWithStatsRow[] {
  const out: DropsWithStatsRow[] = [];
  const indexByUid = new Map<string, number>();

  for (const row of rows) {
    const uid = String(row.drop.uniqueId ?? '').trim();
    if (!uid) {
      out.push(row);
      continue;
    }

    const idx = indexByUid.get(uid);
    if (idx === undefined) {
      indexByUid.set(uid, out.length);
      out.push(row);
      continue;
    }

    const existing = out[idx];
    const exMatch = getDropLogicalDateISO(existing) === workDate;
    const rowMatch = getDropLogicalDateISO(row) === workDate;
    if (rowMatch && !exMatch) out[idx] = row;
  }

  return out;
}

/** Compute drop sequence 1..N for work-day drops (for display and sort). */
export function computeDropSequenceByUniqueId(
  allRows: DropsWithStatsRow[],
  workDate: string,
): Map<string, number> {
  const workDay: DropsWithStatsRow[] = allRows.filter((r) => getDropLogicalDateISO(r) === workDate);
  const bySendOrder = [...workDay].sort((a, b) => {
    const ka = getDropOrderKey(a);
    const kb = getDropOrderKey(b);
    if (ka !== kb) return ka - kb;
    return a.drop.id - b.drop.id;
  });
  const map = new Map<string, number>();
  bySendOrder.forEach((r, i) => map.set(r.drop.uniqueId, i + 1));
  return map;
}

export function toGenericStatsRowFromDrop(
  row: DropsWithStatsRow,
  dropSequenceByUniqueId: Map<string, number>,
): GenericStatsRow {
  const { drop, voluum } = row;
  const sequence = dropSequenceByUniqueId.get(drop.uniqueId);
  const workDateIso = getDropLogicalDateISO(row) ?? undefined;
  const searchText = [
    orDash(drop.leadsList),
    drop.title ?? '',
    orDash(drop.campaign),
    orDash(drop.smsGatewayRoute),
    orDash(drop.senderName),
    drop.uniqueId ?? '',
  ]
    .join(' ')
    .toLowerCase();
  return {
    id: drop.uniqueId,
    label: orDash(drop.leadsList) || drop.title || drop.uniqueId || '—',
    subLabel: drop.uniqueId ? `#${drop.id}` : undefined,
    voluum,
    campaign: orDash(drop.campaign) || undefined,
    leadsList: orDash(drop.leadsList) || undefined,
    gateway: orDash(drop.smsGatewayRoute) || undefined,
    searchText,
    sequence,
    workDateIso,
    _originalDrop: row,
  };
}
